'use client';
import React, { useRef, useState } from 'react';
import Button from '../button/button';
import { Container, OverlayTrigger, TooltipProps } from 'react-bootstrap';
import Tooltip from '../tooltip/tooltip';
import { HiOutlineSearch } from 'react-icons/hi';
import ToggleMenu from '../toggleMenu/toggleMenu';
import MobileMenuComponent from './mobileMenu';
import Link from 'next/link';
import './header.scss';
import Image from 'next/image';
import DiscoveryTrigger from '@/public/n_icons/discovery-trigger';
import { RiLock2Fill } from 'react-icons/ri';
import { LuLoader } from 'react-icons/lu';
import { imageData } from '@/config/defaultBlur';
import { useUserProfile } from '@/contexts/UserProfileContext';

declare global {
  interface Window {
    sug_memberinfo?: {
      MEMBERID: number | null;
    };
  }
}

export default function Header() {
  const [isVisible, setIsVisible] = useState(false);
  const [MoreMenu, setIsMoreMenu] = useState(false);
  const [MoreLoginMenu, setIsMoreLoginMenu] = useState(false);
  const [MobileMenu, setMobileMenu] = useState(false);
  const moreMenuRef = useRef<HTMLLIElement>(null);
  const moreLoginMenuRef = useRef<HTMLDivElement>(null);
  const extendedWrapRef = useRef<HTMLDivElement | null>(null);
  const { userProfile, userRoles, isLoading, handleLogout } = useUserProfile();

  const renderTooltip = (props: TooltipProps) => (
    <Tooltip tooltipText="Log In or Register" id="overlay-example" {...props} />
  );

  const showMoreMenu = () => {
    setIsMoreMenu((prev) => !prev);
  };

  const showLoginMoreMenu = () => {
    setIsMoreLoginMenu((prev) => !prev);
  };

  const openMobileMenu = () => {
    setMobileMenu((prev) => !prev);
  };

  const clickOnLinkHandle = () => {
    setMobileMenu((prev) => !prev);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        moreMenuRef.current &&
        !moreMenuRef.current.contains(event.target as Node)
      ) {
        setIsMoreMenu(false);
      }
      if (
        moreLoginMenuRef.current &&
        !moreLoginMenuRef.current.contains(event.target as Node)
      ) {
        setIsMoreLoginMenu(false);
      }
    };
    const handlePageReload = () => {
      setIsMoreMenu(false);
      setMobileMenu(false);
      setIsMoreLoginMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('beforeunload', handlePageReload);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('beforeunload', handlePageReload);
    };
  }, []);

  const handleCloseMoreMenu = () => {
    setIsMoreMenu(false);
  };

  const handleCloseLoginMoreMenu = () => {
    setIsMoreLoginMenu(false);
  };

  const toggleVisibility = () => {
    setIsVisible((prevState) => !prevState);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      extendedWrapRef.current &&
      !extendedWrapRef.current.contains(event.target as Node)
    ) {
      setIsVisible(false);
    }
  };

  React.useEffect(() => {
    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible]);

  const renderLoginButton = () => {
    if (isLoading) {
      return (
        <div className="loading">
          <LuLoader />
        </div>
      );
    }
    if (userProfile) {
      return (
        <>
          <div ref={moreLoginMenuRef} className="loginMoreMenuWrap">
            <div
              onClick={showLoginMoreMenu}
              className={`headerMenuLinkBtn ${MoreLoginMenu ? 'menu-show' : 'menu-hide'}`}
              aria-label="Show more options"
            >
              {userProfile.profilepicture.text ? (
                <span className="profile-name">
                  {userProfile.profilepicture.text}
                </span>
              ) : (
                <Image
                  src={userProfile.profilepicture.small || ''}
                  alt="profile"
                  height={34}
                  width={34}
                  className="profile-img"
                  placeholder="blur"
                  blurDataURL={imageData.blurImage}
                />
              )}
            </div>
            <ul
              className={`extendedMenuWrap extendedLoginMenuWrap ${MoreLoginMenu ? 'open-menu' : 'hide-menu'}`}
              role="menu"
              aria-hidden={!MoreLoginMenu}
            >
              <li className="extendedMenu">
                <Link
                  onClick={handleCloseLoginMoreMenu}
                  className="extendedMenuLink"
                  href={`/myaccount`}
                  aria-label="Resources & Tips"
                >
                  Sign Ups
                </Link>
              </li>
              {userProfile && userRoles.length > 0 && (
                <li className="extendedMenu">
                  <Link
                    onClick={handleCloseLoginMoreMenu}
                    className="extendedMenuLink"
                    href={`/MyAccount/ResetAdminID/1?switch=true`}
                    aria-label="Admin"
                  >
                    Switch Account
                  </Link>
                </li>
              )}
              <li className="extendedMenu">
                <Link
                  onClick={handleCloseLoginMoreMenu}
                  className="extendedMenuLink"
                  href={`/index.cfm?go=m.referralProgram&memberid=${userProfile?.id}`}
                  aria-label="Referral Program"
                  id="rplink"
                >
                  Referral Program
                </Link>
              </li>
              <li className="extendedMenu">
                <Link
                  onClick={handleCloseLoginMoreMenu}
                  className="extendedMenuLink"
                  href={`/index.cfm?go=m.editprofile&memberid=${userProfile?.id}`}
                  aria-label="Settings"
                >
                  Settings
                </Link>
              </li>
              <li className="extendedMenu">
                <Link
                  onClick={handleCloseLoginMoreMenu}
                  className="extendedMenuLink"
                  href={`/index.cfm?go=o.mypurchases`}
                  aria-label="Billing"
                >
                  Billing
                </Link>
              </li>
              {!userProfile.ispro && (
                <li className="extendedMenu">
                  <Link
                    onClick={handleCloseLoginMoreMenu}
                    className="extendedMenuLink"
                    href={`/index.cfm?go=o.ProGeniusProducts&showtrial=1`}
                    aria-label="Free Premium Trial"
                  >
                    Free Premium Trial
                  </Link>
                </li>
              )}
              <li className="extendedMenu">
                <Link
                  className="extendedMenuLink"
                  href={`/index.cfm?go=c.logout`}
                  aria-label="Logout"
                  onClick={handleLogout}
                >
                  Log Out
                </Link>
              </li>
              {userProfile?.isadmin && (
                <li className="extendedMenu">
                  <Link
                    onClick={handleCloseLoginMoreMenu}
                    className="extendedMenuLink"
                    href={`/admin`}
                    aria-label="Admin"
                  >
                    <RiLock2Fill /> Admin
                  </Link>
                </li>
              )}
            </ul>
          </div>
          <div
            className={`mobileHeaderWrap ${MobileMenu ? 'show-menu' : 'hide-menu'}`}
            role="region"
            aria-hidden={!MobileMenu}
          >
            <MobileMenuComponent
              openMobileMenu={openMobileMenu}
              clickOnLink={clickOnLinkHandle}
              userProfile={{
                ...userProfile,
                profilepicture: {
                  ...userProfile.profilepicture,
                  text: userProfile.profilepicture.text || '',
                },
              }}
              userRoles={userRoles}
            />
          </div>
        </>
      );
    }
    return (
      <>
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={renderTooltip}
        >
          <Button variant="text" href={`/register`}>
            Log In
          </Button>
        </OverlayTrigger>
        <div
          className={`mobileHeaderWrap ${MobileMenu ? 'show-menu' : 'hide-menu'}`}
          role="region"
          aria-hidden={!MobileMenu}
        >
          <MobileMenuComponent
            openMobileMenu={openMobileMenu}
            clickOnLink={clickOnLinkHandle}
            userProfile={null}
            userRoles={null}
          />
        </div>
      </>
    );
  };

  return (
    <header className="headerStyledMain">
      <Container>
        <div className="headerStyled">
          <div className="signupSolutionWrap" ref={extendedWrapRef}>
            <div
              className="toggleIcon"
              onClick={toggleVisibility}
              role="button"
              aria-label="Toggle Solutions Menu"
              aria-expanded={isVisible}
            >
              <DiscoveryTrigger />
            </div>
            {isVisible && (
              <div
                className="extendedWrap"
                role="region"
                aria-labelledby="solutions-menu"
              >
                <h5>Explore SignUpGenius Solutions</h5>
                <div className="explore-giving">
                  <Link
                    className="discoverLogo"
                    href={process.env.NEXT_PUBLIC_AUCTION_API_URL!}
                    target="_blank"
                    aria-label="Explore Auction - Online Auctions"
                    id="auction"
                  >
                    <Image
                      src="/n_images/explorIcon/auction.png"
                      alt="SugGiving"
                      width={40}
                      height={40}
                      quality={100}
                    />
                    <div className="discoverInfo">
                      <h5>Auctions</h5>
                      <span>Online Auctions</span>
                    </div>
                  </Link>
                  <Link
                    className="discoverLogo"
                    href={process.env.NEXT_PUBLIC_DONATION_API_URL!}
                    target="_blank"
                    aria-label="Explore Giving - Fundraising and donations"
                  >
                    <Image
                      src="/n_images/explorIcon/giving-icon.webp"
                      alt="SugGiving"
                      width={40}
                      height={40}
                      quality={100}
                    />
                    <div className="discoverInfo">
                      <h5>Donations</h5>
                      <span>Fundraising and Donations</span>
                    </div>
                  </Link>
                  <Link
                    className="discoverLogo"
                    href={process.env.NEXT_PUBLIC_TICKET_API_URL!}
                    target="_blank"
                    aria-label="Tickets - Event ticketing"
                  >
                    <Image
                      src="/n_images/explorIcon/tickets-icon.png"
                      alt="SugGiving"
                      width={40}
                      height={40}
                      quality={100}
                    />
                    <div className="discoverInfo">
                      <h5>Tickets</h5>
                      <span>Event Ticketing</span>
                    </div>
                  </Link>
                </div>
              </div>
            )}
          </div>
          <Link href="/" aria-label="Go to home page">
            <Image
              className="logoImage"
              src="/n_images/logo-signupgenius.svg"
              alt={'SignUpGenius-logo'}
              width={144}
              height={45}
              quality={100}
              sizes="(max-width: 767px) 70vw, 10vw"
              priority
            />
          </Link>
          <ul className="headerMenuWrap">
            <li className="headerMenu">
              <Link
                className="headerMenuLink"
                href={`/features`}
                aria-label="Features page"
              >
                Features
              </Link>
            </li>
            <li className="headerMenu">
              <Link
                className="headerMenuLink"
                href={`/pricing`}
                aria-label="Pricing page"
              >
                Pricing
              </Link>
            </li>
            <li className="headerMenu show-d">
              <Link
                className="headerMenuLink"
                href={`/how-to-use/collect-money`}
                aria-label="Collect Money page"
              >
                Collect Money
              </Link>
            </li>
            <li className="headerMenu show-m">
              <Link
                className="headerMenuLink"
                href={`/how-to-use/collect-money`}
                aria-label="Collect Money page"
              >
                Collect
              </Link>
            </li>
            <li className="headerMenu" ref={moreMenuRef}>
              <div
                onClick={showMoreMenu}
                className={`headerMenuLinkBtn ${MoreMenu ? 'menu-show' : 'menu-hide'}`}
                aria-label="Show more options"
              >
                More
              </div>
              <ul
                className={`extendedMenuWrap ${MoreMenu ? 'open-menu' : 'hide-menu'}`}
                role="menu"
                aria-hidden={!MoreMenu}
              >
                <li className="extendedMenu">
                  <Link
                    onClick={handleCloseMoreMenu}
                    className="extendedMenuLink"
                    href="/resources"
                    aria-label="Resources & Tips"
                  >
                    Resources & Tips
                  </Link>
                </li>
                <li className="extendedMenu">
                  <Link
                    onClick={handleCloseMoreMenu}
                    className="extendedMenuLink"
                    href="/blog"
                    aria-label="Blog page"
                  >
                    Blog
                  </Link>
                </li>
                <li className="extendedMenu">
                  <Link
                    onClick={handleCloseMoreMenu}
                    className="extendedMenuLink"
                    href={`/enterprise`}
                    aria-label="Enterprise page"
                  >
                    Enterprise
                  </Link>
                </li>
                <li className="extendedMenu">
                  <Link
                    onClick={handleCloseMoreMenu}
                    className="extendedMenuLink"
                    href="/support"
                    aria-label="Help & Support"
                  >
                    Help
                  </Link>
                </li>
              </ul>
            </li>
          </ul>
          <div className="headerRightWrap">
            <Button
              variant="primary"
              href={`/index.cfm?go=w.Welcome&prior=w.createSignUp&formName=registerForm`}
              className="show-m"
            >
              Create
            </Button>
            <Button
              variant="primary"
              href={`/index.cfm?go=w.Welcome&prior=w.createSignUp&formName=registerForm`}
              className="show-d"
            >
              Create a Sign Up
            </Button>

            {renderLoginButton()}
            <Link
              className="searchLink desktop-search"
              href={`/findasignup`}
              aria-label="Search for a Sign Up"
            >
              <HiOutlineSearch aria-hidden="true" />
            </Link>
            <ToggleMenu
              className={MobileMenu ? '' : 'collapsed'}
              onClick={openMobileMenu}
              aria-label="Toggle mobile menu"
              aria-expanded={MobileMenu}
            />
          </div>
        </div>
      </Container>
    </header>
  );
}
